import { useState, useCallback, useRef, useEffect } from 'react';

export default function usePDFViewer() {
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1.0);
  const [containerWidth, setContainerWidth] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver((entries) => {
      const { width } = entries[0].contentRect;
      setContainerWidth(width);
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  const onDocumentLoadSuccess = useCallback(({ numPages }) => {
    setNumPages(numPages);
    setIsLoading(false);
    setError(null);
  }, []);

  const onDocumentLoadError = useCallback((err) => {
    setError('No se pudo cargar el PDF. Verifica que el archivo esté disponible.');
    setIsLoading(false);
  }, []);

  const goToPreviousPage = useCallback(() => {
    setCurrentPage((prev) => Math.max(prev - 1, 1));
  }, []);

  const goToNextPage = useCallback(() => {
    setCurrentPage((prev) => Math.min(prev + 1, numPages));
  }, [numPages]);

  const goToPage = useCallback((page) => {
    const parsed = parseInt(page, 10);
    if (!isNaN(parsed) && parsed >= 1 && parsed <= numPages) {
      setCurrentPage(parsed);
    }
  }, [numPages]);

  const zoomIn = useCallback(() => {
    setScale((prev) => Math.min(parseFloat((prev + 0.25).toFixed(2)), 3.0));
  }, []);

  const zoomOut = useCallback(() => {
    setScale((prev) => Math.max(parseFloat((prev - 0.25).toFixed(2)), 0.5));
  }, []);

  const resetZoom = useCallback(() => {
    setScale(1.0);
  }, []);

  return {
    numPages,
    currentPage,
    scale,
    containerWidth,
    isLoading,
    error,
    containerRef,
    onDocumentLoadSuccess,
    onDocumentLoadError,
    goToPreviousPage,
    goToNextPage,
    goToPage,
    zoomIn,
    zoomOut,
    resetZoom,
  };
}
